import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, UserCircle, Loader2, Sparkles } from "lucide-react";

// Iter123k211 · Profilo Ginius (preferenze + hint adattivo quando >=10 chiusure).

const TONI = [
  { v: "formale", label: "Formale", hint: "Lei, tono professionale" },
  { v: "neutro", label: "Neutro", hint: "Equilibrato (default)" },
  { v: "informale", label: "Informale", hint: "Tu, tono diretto" },
];
const DETTAGLI = [
  { v: "sintetico", label: "Sintetico", hint: "Punti chiave, brevi frasi" },
  { v: "medio", label: "Medio", hint: "Bilanciato (default)" },
  { v: "esteso", label: "Esteso", hint: "Dettagli tecnici completi" },
];

export default function CoPilotProfilo() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [forn, setForn] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/copilot/profilo");
      setData(r.data);
      setForn(r.data.fornitori_preferiti || "");
    } catch (e) {
      toast.error("Errore caricamento profilo");
    }
  };

  useEffect(() => { load(); }, []);

  const salva = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/copilot/profilo", {
        tono: data.tono, dettaglio: data.dettaglio, fornitori_preferiti: forn,
        ...patch,
      });
      setData(r.data);
      setForn(r.data.fornitori_preferiti || "");
      toast.success("Preferenze salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    }
    setSaving(false);
  };

  if (!data) {
    return <div className="p-6 text-center text-slate-500"><Loader2 className="inline w-5 h-5 animate-spin mr-1" /> Caricamento…</div>;
  }

  return (
    <div className="space-y-5 max-w-2xl" data-testid="copilot-profilo-page">
      <div className="flex items-center gap-2">
        <button data-testid="copilot-profilo-back" onClick={() => navigate("/copilot/mappa")}
          className="p-2 rounded-full hover:bg-slate-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-3xl font-bold text-brand flex items-center gap-2">
          <UserCircle className="w-7 h-7" /> Profilo Ginius
        </h1>
      </div>

      <Card className="p-4" data-testid="profilo-stato">
        <div className="flex items-center gap-3">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${data.adattivo_attivo ? "bg-emerald-500" : "bg-slate-300"}`}>
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-bold">
              {data.adattivo_attivo ? "Modalità adattiva attiva ✨" : "Modalità adattiva non ancora attiva"}
            </div>
            <div className="text-sm text-slate-600">
              Hai completato <b>{data.sopralluoghi_completati}</b> sopralluoghi.
              {!data.adattivo_attivo && ` Ginius imparerà il tuo stile dopo 10 chiusure (ne mancano ${Math.max(0, 10 - data.sopralluoghi_completati)}).`}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="font-bold mb-2">Tono preferito</div>
        <div className="grid grid-cols-3 gap-2">
          {TONI.map((t) => (
            <button key={t.v} data-testid={`profilo-tono-${t.v}`}
              onClick={() => salva({ tono: t.v })}
              disabled={saving}
              className={`p-2 rounded-lg border-2 text-left ${data.tono === t.v ? "border-brand bg-brand/10" : "border-slate-200 hover:border-brand/50"}`}>
              <div className="font-semibold text-sm">{t.label}</div>
              <div className="text-[11px] text-slate-500">{t.hint}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <div className="font-bold mb-2">Livello di dettaglio</div>
        <div className="grid grid-cols-3 gap-2">
          {DETTAGLI.map((t) => (
            <button key={t.v} data-testid={`profilo-dettaglio-${t.v}`}
              onClick={() => salva({ dettaglio: t.v })}
              disabled={saving}
              className={`p-2 rounded-lg border-2 text-left ${data.dettaglio === t.v ? "border-brand bg-brand/10" : "border-slate-200 hover:border-brand/50"}`}>
              <div className="font-semibold text-sm">{t.label}</div>
              <div className="text-[11px] text-slate-500">{t.hint}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <div className="font-bold mb-2">Fornitori preferiti</div>
        <div className="text-xs text-slate-500 mb-2">
          Un fornitore per riga. Ginius li proporrà prima nelle bozze di preventivo quando pertinenti.
        </div>
        <textarea data-testid="profilo-fornitori"
          rows={5} value={forn} onChange={(e) => setForn(e.target.value)}
          placeholder="Es.
Edilizia Rossi Napoli
Impianti Verdi srl
Idraulica Bianchi"
          className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
        <Button data-testid="profilo-fornitori-save" onClick={() => salva({ fornitori_preferiti: forn })}
          disabled={saving}
          className="mt-2 bg-brand hover:bg-brand/90 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Salva fornitori
        </Button>
      </Card>
    </div>
  );
}
